/**
 * PersonalCampaignBanner — Iter 178.12 (Danny Feb 2026)
 *
 * Pinned top-of-queue card showing the rep's auto-built "My Follow-Ups"
 * campaign. Polls `/api/dialer/personal-campaign` and surfaces:
 *   • Stream totals (committed callbacks, decay warnings, voicemail
 *     reminders, hot-lead top-ups)
 *   • One-click "Start calling" that pops the rep into the first
 *     committed callback (highest urgency)
 *
 * Intentionally a small banner for v1 — full integration into
 * CampaignGroupedQueue's grouped layout can come later. Stream details
 * are exposed via an expand-on-click panel beneath the totals.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, Clock, AlertTriangle, Phone, TrendingUp, ChevronRight, ChevronDown } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const STREAM_META = {
  committed:     { label: "Committed callbacks", icon: Clock,         color: "#DC2626", priority: 1 },
  tasks:         { label: "Due & overdue tasks", icon: Clock,         color: "#B45309", priority: 2 },
  decay:         { label: "Lock decay",          icon: AlertTriangle, color: "#EA8A1A", priority: 3 },
  soft_reminder: { label: "Voicemail follow-ups",icon: Phone,         color: "#7C3AED", priority: 4 },
  hot_topup:     { label: "Hot leads",           icon: TrendingUp,    color: "#1D9E75", priority: 5 },
};

export default function PersonalCampaignBanner({ onPickLead, view = "today", ownerId = null }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const token = (typeof window !== "undefined") ? localStorage.getItem("token") : null;

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/dialer/personal-campaign`, {
          headers: { Authorization: `Bearer ${token}` }, params: { view, ...(ownerId ? { owner_id: ownerId } : {}) }, timeout: 12000,
        });
        if (alive) setData(r.data);
      } catch { /* non-fatal — banner just won't render */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [token, view, ownerId]);

  if (loading) return null;
  if (!data || !data.totals || data.totals.all === 0) return null;

  const sortedStreams = Object.entries(STREAM_META)
    .sort((a, b) => a[1].priority - b[1].priority)
    .filter(([k]) => (data.totals[k] || 0) > 0);

  const firstLead = (data.streams.committed[0]
                  || (data.streams.tasks || [])[0]
                  || data.streams.decay[0]
                  || data.streams.soft_reminder[0]
                  || data.streams.hot_topup[0]);

  return (
    <div
      data-testid="personal-campaign-banner"
      style={{
        background: "linear-gradient(135deg, #1E3A8A 0%, #312E81 100%)",
        color: "#fff",
        borderRadius: 12,
        padding: "16px 18px",
        marginBottom: 12,
        boxShadow: "0 4px 16px rgba(30,58,138,0.25)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <Sparkles style={{ width: 14, height: 14, color: "#FDE68A" }} />
        <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.04em" }}>
          My Follow-Ups · {data.totals.all} leads
        </span>
        <span style={{ marginLeft: "auto", fontSize: 10, color: "#C7D2FE" }}>
          AI-built for you
        </span>
      </div>

      {/* Iter 178.20 (spec §1B) — Morning briefing summary line. */}
      {data.morning_briefing && (
        <div
          data-testid="personal-campaign-morning-briefing"
          style={{
            fontSize: 12,
            color: "#E0E7FF",
            marginBottom: 10,
            lineHeight: 1.5,
            fontStyle: "italic",
          }}
        >
          {data.morning_briefing}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        {sortedStreams.map(([key, meta]) => {
          const Icon = meta.icon;
          return (
            <div
              key={key}
              data-testid={`personal-campaign-stream-${key}`}
              style={{
                background: "rgba(255,255,255,0.1)",
                border: `0.5px solid ${meta.color}`,
                padding: "5px 10px",
                borderRadius: 999,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 11,
                fontWeight: 500,
              }}
            >
              <Icon style={{ width: 10, height: 10, color: meta.color }} />
              <span style={{ color: "#fff" }}>
                <strong>{data.totals[key]}</strong> {meta.label}
              </span>
            </div>
          );
        })}
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        {firstLead && (
          <button
            type="button"
            data-testid="personal-campaign-start-calling"
            onClick={() => onPickLead?.(firstLead)}
            style={{
              padding: "7px 14px", background: "#fff", color: "#1E3A8A",
              border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600,
              cursor: "pointer", fontFamily: "inherit",
              display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            <Phone style={{ width: 11, height: 11 }} />
            Start calling
          </button>
        )}
        <button
          type="button"
          data-testid="personal-campaign-toggle-detail"
          onClick={() => setExpanded(v => !v)}
          style={{
            padding: "7px 12px", background: "transparent", color: "#C7D2FE",
            border: "0.5px solid rgba(199,210,254,0.4)", borderRadius: 8,
            fontSize: 11, fontWeight: 500, cursor: "pointer", fontFamily: "inherit",
            display: "inline-flex", alignItems: "center", gap: 4,
          }}
        >
          {expanded ? <ChevronDown style={{width:10, height:10}}/> : <ChevronRight style={{width:10, height:10}}/>}
          {expanded ? "Hide details" : "Show leads"}
        </button>
      </div>

      {expanded && (
        <div data-testid="personal-campaign-detail" style={{ marginTop: 12, paddingTop: 12, borderTop: "0.5px solid rgba(255,255,255,0.15)" }}>
          {sortedStreams.map(([key, meta]) => (
            <div key={key} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: meta.color, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
                {meta.label}
              </div>
              {(data.streams[key] || []).slice(0, 5).map(lead => (
                <button
                  key={lead.id}
                  type="button"
                  data-testid={`personal-campaign-lead-${lead.id}`}
                  onClick={() => onPickLead?.(lead)}
                  style={{
                    display: "flex", alignItems: "center", gap: 8, width: "100%",
                    padding: "5px 8px", background: "rgba(255,255,255,0.05)",
                    border: "none", borderRadius: 6, color: "#fff", fontFamily: "inherit",
                    fontSize: 11, marginBottom: 3, cursor: "pointer", textAlign: "left",
                  }}
                >
                  <Phone style={{ width: 9, height: 9, color: meta.color }} />
                  <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {lead.company || "—"}
                  </span>
                  {/* Iter 227 (spec §1C row 3) — Sparkles badge on
                      AI-generated tasks so reps can see at-a-glance
                      which callbacks came from Whisper/GPT extraction
                      vs. their own manual scheduling. */}
                  {lead.ai_generated && (
                    <span
                      data-testid={`personal-campaign-ai-badge-${lead.id}`}
                      title="AI-generated from a call transcript"
                      style={{ display: "inline-flex", alignItems: "center", gap: 2, padding: "1px 4px", background: "rgba(250,204,21,0.18)", borderRadius: 4, fontSize: 9, color: "#FCD34D", fontWeight: 600 }}
                    >
                      <Sparkles style={{ width: 8, height: 8 }} /> AI
                    </span>
                  )}
                  <span style={{ fontSize: 10, color: "#C7D2FE" }}>{lead.stream_reason}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
